// src/hooks/useApi.js
import { useState, useEffect, useCallback } from 'react';

/**
 * Generic data-fetching hook.
 * @param {Function} fetchFn   – async function that returns data
 * @param {Array}    deps      – dependency array (like useEffect)
 * @param {any}      initial   – initial data value (default [])
 */
export function useApi(fetchFn, deps = [], initial = []) {
  const [data,    setData]    = useState(initial);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load, setData };
}

/** Spinner component */
export function Spinner() {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
      ⏳ Loading…
    </div>
  );
}

/** Error banner */
export function ErrBanner({ msg }) {
  return (
    <div style={{ padding: 12, background: '#fff5f5', border: '1px solid #fed7d7',
                  borderRadius: 6, color: '#c53030', marginBottom: 12 }}>
      ⚠️ {msg}
    </div>
  );
}

/** Today's date in YYYY-MM-DD */
export function today() {
  return new Date().toISOString().slice(0, 10);
}

/** Format date YYYY-MM-DD (or Date object) → DD/MM/YYYY */
export function fmtDate(val, withTime = false) {
  if (!val) return '—';
  
  try {
    const dt = (val instanceof Date) ? val : new Date(val);
    if (isNaN(dt.getTime())) return val;
    
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    
    let res = `${d}/${m}/${y}`;
    if (withTime) {
      const hh = String(dt.getHours()).padStart(2, '0');
      const mm = String(dt.getMinutes()).padStart(2, '0');
      res += ` ${hh}:${mm}`;
    }
    return res;
  } catch (e) {
    return val;
  }
}

/** Format number to 2 decimal places with INR locale */
export function fmtNum(val) {
  return Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
